import { TrashIcon } from "@heroicons/react/24/outline";
import EllipsisHorizontalCircleIcon from "@heroicons/react/24/outline/EllipsisHorizontalCircleIcon";
import HandThumbDownIcon from "@heroicons/react/24/outline/HandThumbDownIcon";
import HandThumbUpIcon from "@heroicons/react/24/outline/HandThumbUpIcon";
import { Link, useRouteContext } from "@tanstack/react-router";
import { EditorContent, type JSONContent } from "@tiptap/react";
import { useMeasure } from "@uidotdev/usehooks";
import { useMemo, useState } from "react";
import { graphql, useFragment, useMutation } from "react-relay";
import { Icon } from "@/components/icon";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog } from "@/components/ui/dialog";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { JoinModal } from "@/dialogs/join-dialog";
import { useEditor } from "@/editor/editor";
import { cn } from "@/utils/cn";
import type { commentPreviewCreateCommentVoteMutation } from "./__generated__/commentPreviewCreateCommentVoteMutation.graphql";
import type { commentPreviewDeleteCommentMutation } from "./__generated__/commentPreviewDeleteCommentMutation.graphql";
import type { commentPreviewFragment$key } from "./__generated__/commentPreviewFragment.graphql";

function CommentContent({ content }: { content: JSONContent }) {
	const [editorRef, editorMeasure] = useMeasure();
	const [containerRef, containerMeasure] = useMeasure();
	const [showContent, setShowContent] = useState(false);

	const { club, session } = useRouteContext({ from: "__root__" });

	const isScrollable = useMemo(() => {
		if (showContent) {
			return true;
		}

		return (editorMeasure?.height || 0) > (containerMeasure?.height || 0);
	}, [editorMeasure, containerMeasure, showContent]);

	const editor = useEditor({
		editable: false,
		content: content,
		session,
		club: club.slug,
		features: {
			colors: { enabled: true },
			button: { enabled: true },
			mention: { enabled: true },
			image: { enabled: true },
		},
	});

	return (
		<div
			className={cn(
				"overflow-y-hidden relative w-full flex-1 bg-muted p-4 rounded",
				!showContent ? "max-h-72" : "h-fit",
			)}
			ref={containerRef}
		>
			{editor ? <EditorContent editor={editor!} ref={editorRef} /> : null}

			{isScrollable && showContent ? (
				<div className="bg-zinc-100 dark:bg-zinc-700 w-full mt-6">
					<Button
						type="button"
						variant="ghost"
						onClick={() => setShowContent(!showContent)}
					>
						{showContent ? "Ver Menos" : "Ver Mais"}
					</Button>
				</div>
			) : isScrollable ? (
				<div className="absolute bottom-0 w-full">
					<div className="w-full bg-linear-to-t from-zinc-100 dark:from-zinc-700 to-transparent h-16" />
					<div className="bg-zinc-100 dark:bg-zinc-700 w-full pt-2">
						<Button
							type="button"
							variant="ghost"
							onClick={() => setShowContent(!showContent)}
						>
							{showContent ? "Ver Menos" : "Ver Mais"}
						</Button>
					</div>
				</div>
			) : null}
		</div>
	);
}

export function CommentPreview({
	comment,
}: {
	comment: commentPreviewFragment$key;
}) {
	const { club, session } = useRouteContext({ from: "__root__" });
	const [joinModalOpen, setJoinModalOpen] = useState(false);
	const data = useFragment<commentPreviewFragment$key>(
		graphql`
    fragment commentPreviewFragment on CommentInterface {
      id
      content

      author {
        id
        displayName
        avatarFallback
        photo {
          signedUrl
        }
      }

      votes
      userVote {
        vote
      }

      ... on Comment {
        post {
          title
          slug
          space {
            slug
          }
        }
      }

      ... on EventComment {
        event {
          title
          slug
        }
      }

      __typename
    }
  `,
		comment,
	);

	const [vote] = useMutation<commentPreviewCreateCommentVoteMutation>(graphql`
	  mutation commentPreviewCreateCommentVoteMutation($commentId: ID!, $vote: PostVoteType!) {
			registerCommentVote(commentId: $commentId, vote: $vote) {
				...commentPreviewFragment
			}
		}
	`);

	const [deleteComment] =
		useMutation<commentPreviewDeleteCommentMutation>(graphql`
	  mutation commentPreviewDeleteCommentMutation($commentId: ID!) {
    	deleteComment(id: $commentId) {
    		id @deleteRecord
    	}
		}
	`);

	const canDelete =
		Boolean(data.author?.id && data.author.id === session?.user?.id) ||
		(club.permissions?.length || 0) > 0;

	const title = () => {
		if (data.post) {
			return (
				<Link
					to="/comunidade/s/$space/posts/$post"
					params={{ space: data.post?.space?.slug!, post: data.post?.slug }}
					className="font-medium"
				>
					{data?.post?.title || data?.event?.title}{" "}
					<Icon name="MoveUpRight" className="size-3 inline pb-1" />
				</Link>
			);
		} else if (data.event) {
			return (
				<Link
					to="/eventos/$slug"
					params={{ slug: data.event.slug }}
					className="font-medium"
				>
					{data?.event?.title}{" "}
					<Icon name="MoveUpRight" className="size-3 inline pb-1" />
				</Link>
			);
		}
	};

	return (
		<Dialog open={joinModalOpen} onOpenChange={setJoinModalOpen}>
			<Card className="p-6 prose dark:prose-invert w-full min-w-full gap-6 flex flex-col">
				<div className="text-sm/5 text-foreground">
					<span className="font-medium">{data.author?.displayName}</span>{" "}
					comentou no {data.post ? "post" : "evento"} {title()}
				</div>

				<CommentContent content={data.content} />

				<div className="flex flex-row justify-between">
					<div className="flex flex-row items-center">
						<div className="flex flex-row items-center justify-center w-fit gap-0">
							<Button
								type="button"
								variant="ghost"
								className={cn(
									data.userVote?.vote === "UP" &&
										"text-green-600 dark:text-green-500",
								)}
								onClick={() => {
									if (!session?.user) {
										setJoinModalOpen(true);
										return;
									}

									vote({ variables: { commentId: data.id, vote: "UP" } });
								}}
							>
								<HandThumbUpIcon className="size-5" />
							</Button>

							<div className="text-center text-base/6 w-fit px-2 foreground font-medium">
								{data.votes}
							</div>

							<Button
								variant="ghost"
								type="button"
								className={cn(
									data.userVote?.vote === "DOWN" &&
										"text-red-600 dark:text-red-500",
								)}
								onClick={() => {
									if (!session?.user) {
										setJoinModalOpen(true);
										return;
									}

									vote({ variables: { commentId: data.id, vote: "DOWN" } });
								}}
							>
								<HandThumbDownIcon className="size-5" />
							</Button>

							{canDelete ? (
								<DropdownMenu>
									<DropdownMenuTrigger asChild>
										<Button
											size="icon"
											variant="ghost"
											aria-label="Opções do comentário"
										>
											<EllipsisHorizontalCircleIcon className="size-5" />
										</Button>
									</DropdownMenuTrigger>

									<DropdownMenuContent>
										<DropdownMenuItem
											variant="destructive"
											onClick={() => {
												if (
													confirm(
														"Tem certeza que deseja deletar este comentário? Essa ação não poderá ser desfeita.",
													)
												) {
													deleteComment({ variables: { commentId: data.id } });
												}
											}}
										>
											<TrashIcon />
											Deletar Comentário
										</DropdownMenuItem>
									</DropdownMenuContent>
								</DropdownMenu>
							) : null}
						</div>
					</div>
				</div>
			</Card>

			<JoinModal />
		</Dialog>
	);
}
